import { QualityRecordStatus } from '@/entities/Document';
import type { ListGeneratedRecordsParams } from '@/entities/GeneratedRecord';

/**
 * The filter of the Generated Records tab. Dates are civil days (`YYYY-MM-DD`), already serialized
 * by the panel, and `filledAt` is the day of the last fill, which the screen calls "Data de
 * Conclusão".
 */
export interface GeneratedRecordFilters {
  status?: QualityRecordStatus | '';
  stageId?: number | null;
  createdAt?: string | null;
  filledAt?: string | null;
}

type GeneratedRecordFilterParams = Pick<
  ListGeneratedRecordsParams,
  'status' | 'stageId' | 'createdAt' | 'filledAt'
>;

const LISTED_STATUS_BY_QUALITY_RECORD_STATUS: Partial<
  Record<QualityRecordStatus, NonNullable<ListGeneratedRecordsParams['status']>>
> = {
  [QualityRecordStatus.DISPONIVEL]: 'AVAILABLE',
  [QualityRecordStatus.EM_USO]: 'IN_USE',
};

export const GENERATED_RECORD_FILTER_STATUSES = [
  QualityRecordStatus.DISPONIVEL,
  QualityRecordStatus.EM_USO,
] as const;

const CIVIL_DAY = /^\d{4}-\d{2}-\d{2}$/;

const civilDayOrUndefined = (value: string | null | undefined) =>
  value && CIVIL_DAY.test(value) ? value : undefined;

// An empty field never reaches the wire: the route validates any value it gets, and `status=` or
// `createdAt=` would answer 400 instead of listing everything.
export function toGeneratedRecordFilterParams(
  filters: GeneratedRecordFilters
): GeneratedRecordFilterParams {
  const params: GeneratedRecordFilterParams = {};
  const status = filters.status ? LISTED_STATUS_BY_QUALITY_RECORD_STATUS[filters.status] : undefined;
  const createdAt = civilDayOrUndefined(filters.createdAt);
  const filledAt = civilDayOrUndefined(filters.filledAt);

  if (status) params.status = status;
  if (typeof filters.stageId === 'number' && Number.isInteger(filters.stageId) && filters.stageId > 0) {
    params.stageId = filters.stageId;
  }
  if (createdAt) params.createdAt = createdAt;
  if (filledAt) params.filledAt = filledAt;

  return params;
}

const isRealCivilDay = (value: string) => {
  if (!CIVIL_DAY.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

const firstValue = (value: unknown) => (Array.isArray(value) ? value[0] : value);

// A URL is typed by hand or left over in the history: only what the panel itself could have
// produced becomes a filter, so the button never shows active for a value that is not applied.
export function parseGeneratedRecordFilter(query: Record<string, unknown>): GeneratedRecordFilters {
  const filters: GeneratedRecordFilters = {};
  const status = firstValue(query.status);
  const stageId = firstValue(query.stageId);
  const createdAt = firstValue(query.createdAt);
  const filledAt = firstValue(query.filledAt);

  const listedStatus = GENERATED_RECORD_FILTER_STATUSES.find((candidate) => candidate === status);
  if (listedStatus) filters.status = listedStatus;
  if (typeof stageId === 'string' && /^[1-9]\d*$/.test(stageId) && Number.isSafeInteger(Number(stageId))) {
    filters.stageId = Number(stageId);
  }
  if (typeof createdAt === 'string' && isRealCivilDay(createdAt)) filters.createdAt = createdAt;
  if (typeof filledAt === 'string' && isRealCivilDay(filledAt)) filters.filledAt = filledAt;

  return filters;
}

export const GENERATED_RECORD_FILTER_QUERY_KEYS = ['status', 'stageId', 'createdAt', 'filledAt'] as const;

export function toGeneratedRecordFilterQuery(
  filters: GeneratedRecordFilters
): Record<(typeof GENERATED_RECORD_FILTER_QUERY_KEYS)[number], string | undefined> {
  const valid = parseGeneratedRecordFilter({
    status: filters.status,
    stageId: filters.stageId === null || filters.stageId === undefined ? undefined : String(filters.stageId),
    createdAt: filters.createdAt,
    filledAt: filters.filledAt,
  });

  return {
    status: valid.status,
    stageId: valid.stageId === undefined ? undefined : String(valid.stageId),
    createdAt: valid.createdAt ?? undefined,
    filledAt: valid.filledAt ?? undefined,
  };
}
